import React from "react";
import UseCaseTemplate from "./UseCaseTemplate";
import hotelHero from "./assets/hotel/hotel_general.png";
import hotel1 from "./assets/hotel/hotel1.jpg";
import hotel2 from "./assets/hotel/hotel2.jpg";
import hotel3 from "./assets/hotel/hotel3.jpg";
import hotel4 from "./assets/hotel/hotel4.jpg";
import hotel5 from "./assets/hotel/hotel5.jpg";

const experiences = [
  {
    id: "living-table",
    number: "01",
    category: "F&B / hotel restaurant",
    title: "The Living Table & Seasonal Menu",
    lead: "Update only the dish that changed instead of reshooting the entire restaurant video every season.",
    problem: "Hotel restaurant videos become obsolete whenever the seasonal menu changes, while full video reshoots are cost-prohibitive.",
    layout: "Storyboard",
    layoutDetail: "Sequential, 4 to 5 slots",
    workflowTitle: "A modular living menu",
    workflow: [
      "Dining room atmosphere.",
      "Signature cocktail.",
      "Seasonal starter.",
      "Signature main dish.",
      "Pastry chef’s dessert.",
      "With each new season, the chef records only the updated dish; the platform replaces that slot without changing the video URL or table QR codes.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Eliminates recurring production costs and drives direct dining reservations.",
    outcome2Label: "Operational value",
    outcome2: "One permanent QR code can remain current while weekly creations and seasonal specials are swapped in from the kitchen pass.",
    image: hotel1,
  },
  {
    id: "mice-spaces",
    number: "02",
    category: "Corporate MICE",
    title: "B2B Sales & Corporate MICE",
    lead: "Show planners how the same ballroom transforms across different configurations before they schedule a site visit.",
    problem: "Corporate event planners struggle to visualize the different layout possibilities of a single ballroom or conference hall.",
    layout: "Versus",
    layoutDetail: "Synchronized 50/50 split-screen",
    workflowTitle: "One room, two synchronized configurations",
    workflow: [
      "Compare Conference / Theater Setup in daytime with Gala Dinner / Lounge Setup in the evening.",
      "Use a synchronized side-by-side view to demonstrate spatial versatility and lighting transitions.",
      "Sales representatives send a single dynamic link instead of static floor-plan PDFs and brochures.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Shortens the sales cycle for corporate seminars and private venue rentals.",
    outcome2Label: "Sales value",
    outcome2: "Supports frictionless remote site inspections and faster proposal sign-offs for international planners and corporate bookers.",
    image: hotel2,
  },
  {
    id: "behind-scenes",
    number: "03",
    category: "Staff & brand storytelling",
    title: "Living Behind-the-Scenes",
    lead: "Turn everyday hospitality craft into an authentic, self-updating brand story.",
    problem: "Traditional corporate hotel videos lack authenticity and remain frozen in time for years.",
    layout: "Symphony",
    layoutDetail: "Pacing driven by ambient soundscapes and music",
    workflowTitle: "Staff-led micro-missions",
    workflow: [
      "Capture the barista pulling the first espresso at 7:00 AM.",
      "Capture the arrival of fresh lobby flowers.",
      "Capture staff lighting outdoor fire pits at dusk.",
      "Weavevo AI synchronizes acoustic signatures and harmonizes color profiles across varied smartphone models.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Self-updating video feeds for lobby displays and websites that highlight human hospitality.",
    outcome2Label: "Operational value",
    outcome2: "On-duty teams create verified micro-moments without disrupting operations or paying for external video crews.",
    image: hotel3,
  },
  {
    id: "digital-concierge",
    number: "04",
    category: "Concierge & local partners",
    title: "The Digital Concierge & Local Micro-Guides",
    lead: "Translate insider recommendations into a collaborative video trail that local partners can keep current.",
    problem: "Static written concierge recommendations lack engagement, and local brand partnerships receive limited visual exposure.",
    layout: "Relay / Gallery",
    layoutDetail: "Kinetic hand-off from the hotel to geofenced partners",
    workflowTitle: "A living local guide",
    workflow: [
      "The concierge initiates the Weave at the hotel entrance.",
      "The narrative baton passes to nearby partner businesses such as an art gallery, boat club or artisan chocolatier.",
      "Partners update their own operating hours or special promotions inside their assigned slot.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Monetizes sponsored video slots with local merchant partners and elevates guest satisfaction.",
    outcome2Label: "Guest value",
    outcome2: "Guests access episodic, smartphone-ready local micro-guides instead of outdated print maps and static PDFs.",
    image: hotel4,
  },
  {
    id: "collaborative-weddings",
    number: "05",
    category: "Private events",
    title: "Collaborative Weddings & Private Events",
    lead: "Compile a same-evening living event story from the hotel, vendors and guests without waiting weeks for post-production.",
    problem: "Collecting and organizing raw footage from florists, DJs, caterers and guests takes weeks for hotel event coordinators.",
    layout: "Seed",
    layoutDetail: "Prompt-driven distributed slots",
    workflowTitle: "Multi-vendor event blueprint",
    workflow: [
      "Create the event blueprint: Ceremony, Terrace cocktail, Cake cutting.",
      "Each vendor submits a 10-second clip.",
      "AI Content Guard verifies resolution and automatically blurs guests requesting privacy.",
      "The finished Weave compiles autonomously the same evening.",
    ],
    outcome1Label: "B2B impact",
    outcome1: "Delivers an up-to-date luxury portfolio after every reception to attract prospective wedding and event clients.",
    outcome2Label: "Client value",
    outcome2: "A turnkey, chapter-marked living keepsake can be delivered the same evening, supporting word-of-mouth and venue re-bookings.",
    image: hotel5,
  },
];

export default function HotelPage() {
  return (
    <UseCaseTemplate
      pageTitle={"Hotel"}
      currentPath={"/use-cases/hotel"}
      eyebrow={"WEAVEVO USE CASE · HOTEL"}
      heroTitle={"Turn hotel operations into living guest experiences."}
      heroText={"Keep restaurant menus, event spaces, staff stories, local concierge guides and private-event media current through modular living content captured by the people already on site."}
      heroAlt={"Hotel Weavevo concept with living hospitality content"}
      finalEyebrow={"BUILD THE NEXT HOTEL WEAVE"}
      finalTitle={"Your hotel changes every day. Its media can change with it."}
      finalText={"Use modular living media to keep dining, MICE, concierge, staff storytelling and private-event experiences continuously relevant."}
      heroImage={hotelHero}
      experiences={experiences}
    />
  );
}
